import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { 
  GoogleIcon, 
  GitHubIcon, 
  FacebookIcon, 
  AppleIcon, 
  MicrosoftIcon 
} from "@/components/auth/OAuthIcons";
import { 
  initiateGoogleAuth,
  initiateGitHubAuth,
  initiateFacebookAuth,
  initiateAppleAuth,
  initiateMicrosoftAuth,
  validateOAuthConfig
} from "@/utils/oauth";
import { orderByLastUsed } from "@/utils/lastLoginProvider";

// Pill shown on the sign-in method the user last succeeded with
export const LastUsedBadge = () => (
  <span aria-hidden="true" className="pointer-events-none absolute -top-2.5 right-3 z-10 rounded-full bg-gradient-brand px-2 py-0.5 text-[10px] font-semibold leading-4 text-white shadow-sm">
    Last used
  </span>
);

interface OAuthButtonsProps {
  isSignupPage?: boolean;
  disableSignup?: boolean;
  // Login page only: the last successful provider, shown first with a "Last used" badge
  lastUsedProvider?: string | null;
}

export const OAuthButtons = ({ isSignupPage = false, disableSignup = false, lastUsedProvider = null }: OAuthButtonsProps) => {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleOAuthLogin = async (provider: string) => {
    // TEMPORARY: Block signup attempts only when on signup page and signup is disabled
    if (isSignupPage && disableSignup) {
      toast({
        variant: "destructive",
        title: "Signup Not Available",
        description: "Signup is temporarily disabled. The platform will be available soon!",
      });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get('rd');
    if (redirectUrl) {
      localStorage.setItem('oauth_redirect_after', redirectUrl);
    }

    setLoadingProvider(provider);
    
    try {

      
      // Validate configuration for this specific provider
      const config = validateOAuthConfig(provider);
      if (!config.valid) {
        throw new Error(`Missing OAuth configuration: ${config.missing.join(', ')}`);
      }
      
      let authUrl: string;
      
      switch (provider) {
        case 'google':
          authUrl = initiateGoogleAuth();
          break;
        case 'github':
          authUrl = initiateGitHubAuth();
          break;
        case 'facebook':
          authUrl = initiateFacebookAuth();
          break;
        case 'apple':
          authUrl = initiateAppleAuth();
          break;
        case 'microsoft':
          authUrl = initiateMicrosoftAuth();
          break;
        default:
          throw new Error('Unknown provider');
      }


      
      // Verify sessionStorage was written correctly (oauth.ts uses sessionStorage)
      const storedState = sessionStorage.getItem('oauth_state');

      if (!storedState) {
        throw new Error('Failed to store OAuth state');
      }
      
      // Redirect to OAuth provider
      window.location.href = authUrl;
      
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: `Failed to connect with ${provider}. Please try again.`,
      });
      setLoadingProvider(null);
    }
  };

  const oauthProviders = [
    {
      id: 'google',
      name: 'Google',
      icon: GoogleIcon,
      className: 'border-border hover:border-red-400/50 hover:bg-red-500/10 text-foreground hover:text-red-600 dark:hover:text-red-400'
    },
    {
      id: 'github',
      name: 'GitHub',
      icon: GitHubIcon,
      className: 'border-border hover:border-gray-400/50 hover:bg-gray-500/10 text-foreground hover:text-gray-700 dark:hover:text-gray-300'
    },
    // {
    //   id: 'facebook',
    //   name: 'Facebook',
    //   icon: FacebookIcon,
    //   className: 'border-border hover:border-blue-400/50 hover:bg-blue-500/10 text-foreground hover:text-blue-600 dark:hover:text-blue-400'
    // },
    // {
    //   id: 'apple',
    //   name: 'Apple',
    //   icon: AppleIcon,
    //   className: 'border-border hover:border-gray-400/50 hover:bg-gray-500/10 text-foreground hover:text-gray-700 dark:hover:text-gray-300'
    // },
    {
      id: 'microsoft',
      name: 'Microsoft',
      icon: MicrosoftIcon,
      className: 'border-border hover:border-blue-400/50 hover:bg-blue-500/10 text-foreground hover:text-blue-600 dark:hover:text-blue-400'
    }
  ];

  // A remembered provider that is no longer offered (e.g. disabled Apple) is ignored by orderByLastUsed
  const { items: orderedProviders, lastUsedId } = orderByLastUsed(oauthProviders, lastUsedProvider);

  const renderButton = (provider: (typeof oauthProviders)[number], isLastUsed: boolean) => {
    const Icon = provider.icon;
    const isLoading = loadingProvider === provider.id;
    const isDisabled = loadingProvider !== null || (isSignupPage && disableSignup);

    return (
      <Button
        key={provider.id}
        variant="outline"
        onClick={() => handleOAuthLogin(provider.id)}
        disabled={isDisabled}
        aria-label={`Continue with ${provider.name}${isLastUsed ? " (last used)" : ""}`}
        data-last-used={isLastUsed || undefined}
        className={`relative w-full min-w-0 h-11 justify-center gap-2 rounded-[10px] border-input bg-surface text-foreground hover:bg-soft hover:text-foreground transition-colors ${isLastUsed ? 'border-plum ring-1 ring-plum/40' : ''} ${(isSignupPage && disableSignup) ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {isLastUsed && <LastUsedBadge />}
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Icon className="h-4 w-4" />
        )}
        <span className="text-[13px] font-semibold">
          {isLoading ? "Connecting..." : isLastUsed ? `Continue with ${provider.name}` : provider.name}
        </span>
      </Button>
    );
  };

  if (lastUsedId) {
    const [lastUsed, ...others] = orderedProviders;
    // Flex row instead of a fixed grid so the remaining buttons fill the row for any provider count
    return (
      <div className="space-y-2.5">
        {renderButton(lastUsed, true)}
        <div className="flex flex-col gap-2.5 sm:flex-row">
          {others.map((provider) => renderButton(provider, false))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
      {orderedProviders.map((provider) => renderButton(provider, false))}
    </div>
  );
};